// UltraFlex Admin — Products (list → drill-to-card with Pricing, Stock, Bundle, Variants tabs)
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import PermissionGate from '../../components/PermissionGate';
import { Package, Search, ChevronRight, X, Plus, Trash2, Calendar, MapPin, Edit3, ArrowLeft, Layers, Wand2, Tag } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function AdminProducts() {
  const { token } = useAuth();
  const [list, setList] = useState({ items: [], total: 0, page: 1, total_pages: 1 });
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [productType, setProductType] = useState('');
  const [categories, setCategories] = useState([]);
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);
  const [hideVariants, setHideVariants] = useState(true);
  const [autoGroupStatus, setAutoGroupStatus] = useState('');
  const [running, setRunning] = useState(false);

  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  useEffect(() => { fetch(`${API}/api/products/categories`, { headers }).then(r => r.json()).then(d => setCategories(d.categories || [])); }, []);
  useEffect(() => { fetchList(); /* eslint-disable-next-line */ }, [search, category, productType, page, hideVariants]);

  const fetchList = async () => {
    setLoading(true);
    const params = new URLSearchParams({ page, page_size: 50 });
    if (search) params.append('search', search);
    if (category) params.append('category', category);
    if (productType) params.append('product_type', productType);
    if (hideVariants) params.append('hide_variants', 'true');
    const res = await fetch(`${API}/api/products?${params}`, { headers });
    setList(await res.json());
    setLoading(false);
  };

  const runAutoGroupPreview = async () => {
    setRunning(true);
    setAutoGroupStatus('');
    try {
      const res = await fetch(`${API}/api/products/variants/auto-group-safe`, {
        method: 'POST', headers, body: JSON.stringify({ dry_run: true }),
      });
      const d = await res.json();
      setAutoGroupStatus(`Found ${d.high_confidence_groups} high-confidence groups · ${d.children_would_move} products would consolidate. Click "Merge now" to commit.`);
    } finally { setRunning(false); }
  };
  const runAutoGroupNow = async () => {
    if (!window.confirm('Auto-merge all HIGH-confidence variant groups? This will create parent products and move children under them.')) return;
    setRunning(true);
    setAutoGroupStatus('');
    try {
      const res = await fetch(`${API}/api/products/variants/auto-group-safe`, {
        method: 'POST', headers, body: JSON.stringify({ dry_run: false }),
      });
      const d = await res.json();
      setAutoGroupStatus(`✓ Merged ${d.groups_merged} groups · ${d.children_moved} children moved under new parents.`);
      await fetchList();
    } finally { setRunning(false); }
  };

  if (selected) {
    return <ProductCard id={selected.id} onBack={() => { setSelected(null); fetchList(); }} token={token} />;
  }

  return (
    <div className="p-6 min-h-screen bg-uf-bg">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-3xl font-bold text-white">Products</h1>
          <p className="text-uf-textMuted text-sm mt-1">{list.total?.toLocaleString()} {hideVariants ? 'top-level items (variants hidden under parents)' : 'items total'} · click a row to drill into the card</p>
        </div>
        <PermissionGate perm="products.dedupe">
          <div className="flex items-center gap-2">
            {autoGroupStatus && <span className="text-xs text-uf-textMuted max-w-sm">{autoGroupStatus}</span>}
            <button onClick={runAutoGroupPreview} disabled={running} data-testid="auto-group-preview-btn" className="text-xs px-3 py-1.5 bg-uf-surface border border-uf-border hover:border-uf-red text-white rounded flex items-center gap-1.5 disabled:opacity-50">
              <Wand2 className="w-3.5 h-3.5" /> {running ? 'Scanning…' : 'Scan for variants'}
            </button>
            <button onClick={runAutoGroupNow} disabled={running} data-testid="auto-group-now-btn" className="text-xs px-3 py-1.5 bg-uf-red hover:bg-uf-red/90 text-white rounded flex items-center gap-1.5 disabled:opacity-50">
              <Layers className="w-3.5 h-3.5" /> Auto-merge now
            </button>
          </div>
        </PermissionGate>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-4 items-center">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="w-4 h-4 absolute left-3 top-3 text-uf-textMuted" />
          <input
            placeholder="Search name, SKU or barcode..."
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
            data-testid="products-search"
            className="w-full bg-uf-surface border border-uf-border pl-10 pr-4 py-2 text-white rounded focus:border-uf-red focus:outline-none"
          />
        </div>
        <select value={category} onChange={e => { setCategory(e.target.value); setPage(1); }} data-testid="products-category-filter" className="bg-uf-surface border border-uf-border px-3 py-2 text-white rounded">
          <option value="">All categories</option>
          {categories.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <select value={productType} onChange={e => { setProductType(e.target.value); setPage(1); }} data-testid="products-type-filter" className="bg-uf-surface border border-uf-border px-3 py-2 text-white rounded">
          <option value="">All types</option>
          <option value="retail">Retail</option>
          <option value="prepaid_membership">Prepaid Membership</option>
          <option value="day_pass">Day Pass</option>
        </select>
        <label className="flex items-center gap-2 text-uf-textMuted text-sm cursor-pointer" data-testid="products-hide-variants-toggle">
          <input type="checkbox" checked={hideVariants} onChange={e => { setHideVariants(e.target.checked); setPage(1); }} className="accent-uf-red" />
          Hide variants (show parents only)
        </label>
      </div>

      {/* List */}
      <div className="bg-uf-surface border border-uf-border rounded overflow-hidden">
        <div className="grid grid-cols-12 gap-2 px-4 py-3 bg-uf-bg text-uf-textMuted text-xs uppercase tracking-wider font-bold">
          <div className="col-span-5">Name</div>
          <div className="col-span-2">SKU</div>
          <div className="col-span-2">Category</div>
          <div className="col-span-1 text-right">Base £</div>
          <div className="col-span-1 text-right">Stock</div>
          <div className="col-span-1"></div>
        </div>
        {loading && <div className="p-8 text-center text-uf-textMuted">Loading...</div>}
        {!loading && list.items?.length === 0 && <div className="p-8 text-center text-uf-textMuted">No products match your filters</div>}
        {list.items?.map(p => (
          <div key={p.id} onClick={() => setSelected(p)} data-testid={`product-row-${p.id}`}
               className={`grid grid-cols-12 gap-2 px-4 py-3 border-t border-uf-border hover:bg-uf-bg cursor-pointer items-center ${p.is_variant ? 'bg-uf-bg/40 pl-10' : ''}`}>
            <div className="col-span-5 text-white font-medium flex items-center gap-2">
              {p.is_variant ? <Tag className="w-3.5 h-3.5 text-uf-textMuted flex-shrink-0" /> : <Package className="w-4 h-4 text-uf-textMuted flex-shrink-0" />}
              <span className="truncate">{p.name}</span>
              {p.is_variant_parent && <span className="text-[10px] px-1.5 py-0.5 bg-uf-red/20 text-uf-red rounded font-semibold" data-testid={`variant-parent-badge-${p.id}`}>{p.variant_count} VARIANTS</span>}
              {p.product_type === 'prepaid_membership' && <span className="text-[10px] px-1.5 py-0.5 bg-red-500/20 text-red-400 rounded">PREPAID</span>}
              {p.product_type === 'day_pass' && <span className="text-[10px] px-1.5 py-0.5 bg-green-500/20 text-green-400 rounded">DAY PASS</span>}
            </div>
            <div className="col-span-2 text-uf-textMuted text-xs font-mono truncate">{p.sku}</div>
            <div className="col-span-2 text-uf-textMuted text-sm truncate">{p.category}</div>
            <div className="col-span-1 text-right text-white">£{p.price?.toFixed(2)}</div>
            <div className="col-span-1 text-right text-uf-textMuted text-sm">{p.track_inventory ? (p.stock_quantity ?? 0) : '—'}</div>
            <div className="col-span-1 text-right"><ChevronRight className="w-4 h-4 text-uf-textMuted inline" /></div>
          </div>
        ))}
      </div>

      {/* Pagination */}
      {list.total_pages > 1 && (
        <div className="flex items-center justify-between mt-4">
          <div className="text-uf-textMuted text-sm">Page {list.page} of {list.total_pages}</div>
          <div className="flex gap-2">
            <button onClick={() => setPage(Math.max(1, page - 1))} disabled={page === 1} data-testid="products-prev" className="px-3 py-1.5 bg-uf-surface border border-uf-border text-white rounded disabled:opacity-40">Previous</button>
            <button onClick={() => setPage(Math.min(list.total_pages, page + 1))} disabled={page >= list.total_pages} data-testid="products-next" className="px-3 py-1.5 bg-uf-surface border border-uf-border text-white rounded disabled:opacity-40">Next</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ===== PRODUCT CARD (drill-down detail view) =====
function ProductCard({ id, onBack, token }) {
  const [tab, setTab] = useState('details');
  const [product, setProduct] = useState(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [prices, setPrices] = useState([]);
  const [sites, setSites] = useState([]);
  const [newPrice, setNewPrice] = useState({ site_id: '', price: '', effective_from: new Date().toISOString().slice(0, 10), price_level: 'standard' });

  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  useEffect(() => {
    fetch(`${API}/api/products/${id}`, { headers }).then(r => r.json()).then(p => { setProduct(p); setForm(p); });
    fetch(`${API}/api/price-lists/product/${id}`, { headers }).then(r => r.json()).then(d => setPrices(d.items || []));
    fetch(`${API}/api/sites`, { headers }).then(r => r.json()).then(d => setSites((d.items || d || []).filter(s => s.site_type !== 'warehouse')));
  }, [id]);

  const savePrice = async () => {
    if (!newPrice.price) return;
    await fetch(`${API}/api/price-lists`, {
      method: 'POST', headers,
      body: JSON.stringify({ product_id: id, ...newPrice, site_id: newPrice.site_id || null }),
    });
    fetch(`${API}/api/price-lists/product/${id}`, { headers }).then(r => r.json()).then(d => setPrices(d.items || []));
    setNewPrice({ site_id: '', price: '', effective_from: new Date().toISOString().slice(0, 10), price_level: 'standard' });
  };
  const deletePrice = async (pid) => {
    if (!confirm('Delete this price entry?')) return;
    await fetch(`${API}/api/price-lists/${pid}`, { method: 'DELETE', headers });
    setPrices(prices.filter(x => x.id !== pid));
  };
  const saveDetails = async () => {
    const patch = ['name', 'sku', 'barcode', 'category', 'description', 'price', 'cost_price', 'reorder_level', 'vat_category', 'pos_color', 'pos_sort_order', 'is_age_restricted', 'available_online', 'track_inventory', 'is_active'].reduce((a, k) => (form[k] !== undefined ? { ...a, [k]: form[k] } : a), {});
    const res = await fetch(`${API}/api/products/${id}`, { method: 'PATCH', headers, body: JSON.stringify(patch) });
    const updated = await res.json();
    setProduct(updated); setForm(updated); setEditing(false);
  };

  if (!product) return <div className="p-6 text-white">Loading...</div>;

  return (
    <div className="p-6 min-h-screen bg-uf-bg">
      {/* Header */}
      <button onClick={onBack} data-testid="product-card-back" className="flex items-center gap-2 text-uf-textMuted hover:text-white mb-4 text-sm"><ArrowLeft className="w-4 h-4" /> Back to list</button>
      <div className="flex items-start justify-between mb-6">
        <div>
          <div className="text-uf-textMuted text-xs uppercase tracking-wider mb-1">{product.category} · {product.product_type?.replace('_', ' ')}</div>
          <h1 className="font-heading text-3xl font-bold text-white mb-1">{product.name}</h1>
          <div className="text-uf-textMuted text-sm font-mono">{product.sku}{product.barcode && ` · ${product.barcode}`}</div>
        </div>
        <div className="text-right">
          <div className="text-uf-textMuted text-xs uppercase tracking-wider mb-1">Base price</div>
          <div className="text-4xl font-heading font-bold text-uf-red">£{product.price?.toFixed(2)}</div>
        </div>
      </div>

      {/* Category badge + Variant parent indicator */}
      <div className="flex items-center gap-2 mb-5 text-xs">
        <span className="px-2 py-1 bg-uf-surface border border-uf-border rounded text-uf-textMuted flex items-center gap-1"><Tag className="w-3 h-3" /> Category: <span className="text-white font-semibold">{product.category || '—'}</span></span>
        {product.is_variant_parent && <span className="px-2 py-1 bg-uf-red/20 text-uf-red border border-uf-red/30 rounded font-semibold flex items-center gap-1"><Layers className="w-3 h-3" /> VARIANT PARENT · {product.variant_count} children</span>}
        {product.is_variant && <span className="px-2 py-1 bg-blue-500/20 text-blue-400 border border-blue-500/30 rounded font-semibold flex items-center gap-1"><Tag className="w-3 h-3" /> VARIANT {Object.entries(product.variant_attributes || {}).map(([k, v]) => `${k}:${v}`).join(' · ')}</span>}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-4 border-b border-uf-border">
        {[{ k: 'details', l: 'Details' }, { k: 'pricing', l: `Pricing (${prices.length})` }, { k: 'stock', l: 'Stock' }, { k: 'bundle', l: 'Bundle' }, { k: 'variants', l: product.is_variant_parent ? `Variants (${product.variant_count})` : 'Variants · Add' }].map(t =>
          <button key={t.k} onClick={() => setTab(t.k)} data-testid={`product-tab-${t.k}`}
                  className={`px-4 py-2 text-sm font-medium border-b-2 transition ${tab === t.k ? 'border-uf-red text-white' : 'border-transparent text-uf-textMuted hover:text-white'}`}>{t.l}</button>
        )}
      </div>

      {/* DETAILS TAB */}
      {tab === 'details' && (
        <div className="bg-uf-surface border border-uf-border rounded p-6 max-w-3xl">
          <div className="flex justify-end mb-4">
            {!editing ? <PermissionGate perm="products.edit"><button onClick={() => setEditing(true)} data-testid="product-edit-btn" className="px-3 py-1.5 bg-uf-red text-white rounded text-sm flex items-center gap-1"><Edit3 className="w-3 h-3" /> Edit</button></PermissionGate>
              : <div className="flex gap-2"><button onClick={() => { setEditing(false); setForm(product); }} className="px-3 py-1.5 border border-uf-border text-white rounded text-sm">Cancel</button>
                <button onClick={saveDetails} data-testid="product-save-btn" className="px-3 py-1.5 bg-uf-red text-white rounded text-sm">Save</button></div>}
          </div>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <Field label="Name" value={form.name} edit={editing} onChange={v => setForm({ ...form, name: v })} testid="pf-name" />
            <Field label="SKU" value={form.sku} edit={editing} onChange={v => setForm({ ...form, sku: v })} testid="pf-sku" />
            <Field label="Barcode" value={form.barcode} edit={editing} onChange={v => setForm({ ...form, barcode: v })} testid="pf-barcode" />
            <Field label="Category" value={form.category} edit={editing} onChange={v => setForm({ ...form, category: v })} testid="pf-category" />
            <Field label="Base Price (£)" value={form.price} type="number" edit={editing} onChange={v => setForm({ ...form, price: parseFloat(v) })} testid="pf-price" />
            <Field label="Cost Price (£)" value={form.cost_price} type="number" edit={editing} onChange={v => setForm({ ...form, cost_price: parseFloat(v) })} testid="pf-cost" />
            <Field label="Reorder Level" value={form.reorder_level} type="number" edit={editing} onChange={v => setForm({ ...form, reorder_level: parseInt(v) })} testid="pf-reorder" />
            <Field label="VAT Category" value={form.vat_category} edit={editing} onChange={v => setForm({ ...form, vat_category: v })} options={['standard_20', 'zero_rated', 'exempt']} testid="pf-vat" />
            <Field label="POS Colour" value={form.pos_color} edit={editing} onChange={v => setForm({ ...form, pos_color: v })} type="color" testid="pf-color" />
            <Field label="POS Sort Order" value={form.pos_sort_order} type="number" edit={editing} onChange={v => setForm({ ...form, pos_sort_order: parseInt(v) })} testid="pf-sort" />
            <div className="col-span-2 grid grid-cols-4 gap-2 mt-2">
              <Toggle label="Active" value={form.is_active} edit={editing} onChange={v => setForm({ ...form, is_active: v })} testid="pf-active" />
              <Toggle label="Track inventory" value={form.track_inventory} edit={editing} onChange={v => setForm({ ...form, track_inventory: v })} testid="pf-track" />
              <Toggle label="Available online" value={form.available_online} edit={editing} onChange={v => setForm({ ...form, available_online: v })} testid="pf-online" />
              <Toggle label="Age restricted" value={form.is_age_restricted} edit={editing} onChange={v => setForm({ ...form, is_age_restricted: v })} testid="pf-age" />
            </div>
            <div className="col-span-2">
              <Field label="Description" value={form.description} edit={editing} onChange={v => setForm({ ...form, description: v })} multiline testid="pf-desc" />
            </div>
          </div>
        </div>
      )}

      {/* PRICING TAB */}
      {tab === 'pricing' && (
        <div className="bg-uf-surface border border-uf-border rounded p-6 max-w-4xl">
          <PermissionGate perm="stock.price_lists" fallback={
            <div className="text-uf-textMuted text-xs mb-4 italic">You do not have permission to edit price lists — read-only view.</div>
          }>
          <div className="mb-6">
            <h3 className="text-white font-bold mb-1">Add Price</h3>
            <p className="text-uf-textMuted text-xs mb-3">Set a price effective from a date — leave "Site" blank for a global/all-sites price. Future-dated prices apply automatically.</p>
            <div className="grid grid-cols-5 gap-2">
              <select value={newPrice.site_id} onChange={e => setNewPrice({ ...newPrice, site_id: e.target.value })} data-testid="add-price-site" className="bg-uf-bg border border-uf-border px-3 py-2 text-white text-sm rounded">
                <option value="">All Sites (global)</option>
                {sites.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
              <input type="number" step="0.01" placeholder="Price £" value={newPrice.price} onChange={e => setNewPrice({ ...newPrice, price: parseFloat(e.target.value) || '' })} data-testid="add-price-amount" className="bg-uf-bg border border-uf-border px-3 py-2 text-white text-sm rounded" />
              <input type="date" value={newPrice.effective_from} onChange={e => setNewPrice({ ...newPrice, effective_from: e.target.value })} data-testid="add-price-date" className="bg-uf-bg border border-uf-border px-3 py-2 text-white text-sm rounded" />
              <select value={newPrice.price_level} onChange={e => setNewPrice({ ...newPrice, price_level: e.target.value })} data-testid="add-price-level" className="bg-uf-bg border border-uf-border px-3 py-2 text-white text-sm rounded">
                <option value="standard">Standard</option>
                <option value="member">Member</option>
                <option value="staff">Staff</option>
                <option value="student">Student</option>
              </select>
              <button onClick={savePrice} disabled={!newPrice.price} data-testid="add-price-btn" className="bg-uf-red text-white rounded text-sm flex items-center justify-center gap-1 disabled:opacity-40"><Plus className="w-4 h-4" /> Add</button>
            </div>
          </div>
          </PermissionGate>

          {prices.length === 0 && <div className="text-uf-textMuted text-sm text-center py-8">No price list entries yet — uses base price £{product.price?.toFixed(2)}</div>}
          {prices.length > 0 && (
            <div>
              <div className="grid grid-cols-12 gap-2 px-3 py-2 bg-uf-bg text-uf-textMuted text-xs uppercase tracking-wider font-bold rounded-t">
                <div className="col-span-3">Site</div>
                <div className="col-span-2 text-right">Price</div>
                <div className="col-span-2">Effective from</div>
                <div className="col-span-2">Level</div>
                <div className="col-span-2">Status</div>
                <div className="col-span-1"></div>
              </div>
              {prices.map(p => (
                <div key={p.id} data-testid={`price-row-${p.id}`} className="grid grid-cols-12 gap-2 px-3 py-3 border-t border-uf-border items-center text-sm">
                  <div className="col-span-3 flex items-center gap-1 text-white"><MapPin className="w-3 h-3 text-uf-textMuted" />{p.site_name}</div>
                  <div className="col-span-2 text-right text-white font-bold">£{p.price.toFixed(2)}</div>
                  <div className="col-span-2 flex items-center gap-1 text-uf-textMuted"><Calendar className="w-3 h-3" />{p.effective_from}</div>
                  <div className="col-span-2 text-uf-textMuted">{p.price_level}</div>
                  <div className="col-span-2">{p.is_future ? <span className="text-[10px] px-2 py-0.5 bg-amber-500/20 text-amber-400 rounded">Future</span> : <span className="text-[10px] px-2 py-0.5 bg-green-500/20 text-green-400 rounded">Active</span>}</div>
                  <div className="col-span-1 text-right"><PermissionGate perm="stock.price_lists"><button onClick={() => deletePrice(p.id)} data-testid={`delete-price-${p.id}`} className="text-uf-textMuted hover:text-red-400"><Trash2 className="w-4 h-4" /></button></PermissionGate></div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === 'stock' && (
        <div className="bg-uf-surface border border-uf-border rounded p-6 max-w-2xl">
          <div className="mb-4">
            <div className="text-uf-textMuted text-xs uppercase tracking-wider mb-1">Current Stock</div>
            <div className="text-4xl font-heading font-bold text-white">{product.stock_quantity ?? 0}</div>
            <div className="text-uf-textMuted text-sm mt-1">{product.track_inventory ? `Reorder at ${product.reorder_level || 0}` : 'Inventory not tracked'}</div>
          </div>
          <p className="text-uf-textMuted text-sm">Manage stock movements from the <span className="text-white">Admin → Stock</span> page. Every sale, refund, adjustment and stock take writes to the item ledger.</p>
        </div>
      )}

      {tab === 'bundle' && (
        <div className="bg-uf-surface border border-uf-border rounded p-6 max-w-2xl">
          {product.is_bundle ? (
            <div>
              <div className="text-white font-bold mb-2">Bundle components</div>
              {(product.bundle_items || []).map((c, i) => <div key={i} className="text-uf-textMuted text-sm">{c.quantity}× {c.product_id}</div>)}
            </div>
          ) : (
            <p className="text-uf-textMuted text-sm">This is not a bundle. Enable <span className="text-white">is_bundle</span> on the product to configure composite items.</p>
          )}
        </div>
      )}

      {tab === 'variants' && <VariantsTab product={product} token={token} onChange={() => fetch(`${API}/api/products/${id}`, { headers }).then(r => r.json()).then(p => { setProduct(p); setForm(p); })} />}
    </div>
  );
}

// ===== VARIANTS TAB =====
function VariantsTab({ product, token, onChange }) {
  const [data, setData] = useState(null);
  const [schema, setSchema] = useState([]);
  const [adding, setAdding] = useState(false);
  const [newAttrs, setNewAttrs] = useState({});
  const [newPrice, setNewPrice] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const load = async () => {
    const res = await fetch(`${API}/api/products/${product.id}/variants`, { headers });
    if (res.ok) {
      const d = await res.json();
      setData(d);
      setSchema(d.attribute_schema || []);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [product.id]);

  const promoteToParent = async () => {
    if (!window.confirm('Promote this product to a Variant Parent? It will become a grouper for multiple variants.')) return;
    const res = await fetch(`${API}/api/products/${product.id}/promote-parent`, { method: 'POST', headers });
    if (res.ok) { await load(); onChange(); }
  };

  const addVariant = async () => {
    if (!Object.keys(newAttrs).length) { setErr('Pick at least one attribute value'); return; }
    setSaving(true); setErr('');
    try {
      const body = { variant_attributes: newAttrs };
      if (newPrice) body.price = parseFloat(newPrice);
      const res = await fetch(`${API}/api/products/${product.id}/variants`, {
        method: 'POST', headers, body: JSON.stringify(body),
      });
      if (!res.ok) { const j = await res.json().catch(() => ({})); throw new Error(j.detail || 'Add failed'); }
      setNewAttrs({}); setNewPrice(''); setAdding(false);
      await load(); onChange();
    } catch (e) { setErr(e.message); }
    finally { setSaving(false); }
  };

  const removeVariant = async (vid) => {
    if (!window.confirm('Remove this variant? It will be deactivated and unlinked from the parent.')) return;
    const res = await fetch(`${API}/api/products/variants/${vid}`, { method: 'DELETE', headers });
    if (res.ok) { await load(); onChange(); }
  };

  if (!data) return <div className="text-uf-textMuted">Loading variants…</div>;

  // Not yet a variant parent: offer promote CTA
  if (!data.is_variant_parent) {
    return (
      <div className="bg-uf-surface border border-uf-border rounded p-6 max-w-3xl">
        <h3 className="text-white font-bold mb-2">No variants yet</h3>
        <p className="text-uf-textMuted text-sm mb-4">Turn this product into a variant parent to group multiple sizes / colours / flavours under one SKU. Each variant has its own price, barcode, and stock count.</p>
        <p className="text-uf-textMuted text-xs mb-4">Attribute dimensions for <span className="text-white font-semibold">{data.category || 'this category'}</span>: {schema.map(s => s.label).join(', ')}.</p>
        <PermissionGate perm="products.edit">
          <button onClick={promoteToParent} data-testid="promote-parent-btn" className="bg-uf-red text-white px-4 py-2 rounded text-sm flex items-center gap-2"><Layers className="w-4 h-4" /> Promote to Variant Parent</button>
        </PermissionGate>
      </div>
    );
  }

  return (
    <div className="bg-uf-surface border border-uf-border rounded p-6 max-w-4xl">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h3 className="text-white font-bold">{data.variant_count} variants</h3>
          <p className="text-uf-textMuted text-xs mt-1">Attribute dimensions: {schema.map(s => s.label).join(' · ')}</p>
        </div>
        <PermissionGate perm="products.create">
          {!adding && (
            <button onClick={() => setAdding(true)} data-testid="add-variant-btn" className="bg-uf-red text-white px-3 py-1.5 rounded text-sm flex items-center gap-1"><Plus className="w-4 h-4" /> Add Variant</button>
          )}
        </PermissionGate>
      </div>

      {/* Add variant form */}
      {adding && (
        <div className="bg-uf-bg border border-uf-red/30 rounded p-4 mb-5">
          <div className="grid grid-cols-2 gap-3 mb-3">
            {schema.map(dim => (
              <div key={dim.key}>
                <label className="block text-xs font-semibold uppercase tracking-wider text-uf-textMuted mb-1">{dim.label}</label>
                <input
                  list={`dim-${dim.key}`}
                  value={newAttrs[dim.key] || ''}
                  onChange={e => {
                    const v = e.target.value;
                    setNewAttrs(a => { const c = { ...a }; if (v) c[dim.key] = v; else delete c[dim.key]; return c; });
                  }}
                  data-testid={`variant-attr-${dim.key}`}
                  placeholder={`e.g. ${dim.values[0] || 'type value'}`}
                  className="w-full bg-uf-surface border border-uf-border px-3 py-2 text-white rounded text-sm"
                />
                <datalist id={`dim-${dim.key}`}>{dim.values.map(v => <option key={v} value={v} />)}</datalist>
              </div>
            ))}
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-uf-textMuted mb-1">Price £ (optional — inherits parent if blank)</label>
              <input
                type="number" step="0.01"
                value={newPrice} onChange={e => setNewPrice(e.target.value)}
                data-testid="variant-price"
                placeholder={product.price?.toFixed(2)}
                className="w-full bg-uf-surface border border-uf-border px-3 py-2 text-white rounded text-sm"
              />
            </div>
          </div>
          {err && <div className="text-red-400 text-xs mb-3">{err}</div>}
          <div className="flex gap-2">
            <button onClick={addVariant} disabled={saving} data-testid="save-variant-btn" className="bg-uf-red text-white px-3 py-1.5 rounded text-sm flex items-center gap-1 disabled:opacity-50">
              <Plus className="w-4 h-4" /> {saving ? 'Adding…' : 'Add'}
            </button>
            <button onClick={() => { setAdding(false); setNewAttrs({}); setNewPrice(''); setErr(''); }} className="text-uf-textMuted hover:text-white text-sm">Cancel</button>
          </div>
        </div>
      )}

      {/* Variants list */}
      {data.variants.length === 0 && !adding && (
        <div className="text-center py-8 text-uf-textMuted text-sm">No variants yet — click <span className="text-white font-semibold">Add Variant</span> to create the first one.</div>
      )}
      {data.variants.length > 0 && (
        <div className="space-y-1">
          <div className="grid grid-cols-12 gap-2 px-3 py-2 bg-uf-bg text-uf-textMuted text-xs uppercase tracking-wider font-bold rounded">
            <div className="col-span-4">Name / SKU</div>
            <div className="col-span-4">Attributes</div>
            <div className="col-span-2 text-right">Price</div>
            <div className="col-span-1 text-right">Stock</div>
            <div className="col-span-1"></div>
          </div>
          {data.variants.map(v => (
            <div key={v.id} data-testid={`variant-row-${v.id}`} className="grid grid-cols-12 gap-2 px-3 py-3 border-t border-uf-border items-center text-sm hover:bg-uf-bg/50">
              <div className="col-span-4">
                <div className="text-white font-medium truncate">{v.name}</div>
                <div className="text-uf-textMuted text-xs font-mono mt-0.5">{v.sku}</div>
              </div>
              <div className="col-span-4 flex flex-wrap gap-1">
                {Object.entries(v.variant_attributes || {}).map(([k, val]) => (
                  <span key={k} className="text-[10px] px-2 py-0.5 bg-uf-bg border border-uf-border rounded text-uf-textMuted">
                    <span className="text-white">{k}</span>: {val}
                  </span>
                ))}
              </div>
              <div className="col-span-2 text-right text-white">£{v.price?.toFixed(2)}</div>
              <div className="col-span-1 text-right text-uf-textMuted">{v.stock_quantity ?? 0}</div>
              <div className="col-span-1 text-right">
                <PermissionGate perm="products.delete">
                  <button onClick={() => removeVariant(v.id)} data-testid={`remove-variant-${v.id}`} className="text-uf-textMuted hover:text-red-400">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </PermissionGate>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Field({ label, value, edit, onChange, type = 'text', options, multiline, testid }) {
  return (
    <div>
      <label className="text-xs text-uf-textMuted block mb-1">{label}</label>
      {!edit ? <div className="text-white">{value ?? '—'}</div>
        : options ? (
          <select value={value || ''} onChange={e => onChange(e.target.value)} data-testid={testid} className="w-full bg-uf-bg border border-uf-border px-3 py-2 text-white rounded">
            {options.map(o => <option key={o} value={o}>{o}</option>)}
          </select>
        ) : multiline ? (
          <textarea value={value || ''} onChange={e => onChange(e.target.value)} data-testid={testid} rows={3} className="w-full bg-uf-bg border border-uf-border px-3 py-2 text-white rounded" />
        ) : (
          <input type={type} value={value ?? ''} onChange={e => onChange(e.target.value)} data-testid={testid} className="w-full bg-uf-bg border border-uf-border px-3 py-2 text-white rounded" />
        )}
    </div>
  );
}
function Toggle({ label, value, edit, onChange, testid }) {
  return (
    <label className={`flex items-center gap-2 px-3 py-2 rounded border ${value ? 'border-green-500/50 bg-green-500/10' : 'border-uf-border bg-uf-bg'} ${edit ? 'cursor-pointer' : 'cursor-default opacity-80'}`}>
      <input type="checkbox" checked={!!value} disabled={!edit} onChange={e => onChange(e.target.checked)} data-testid={testid} />
      <span className={`text-sm ${value ? 'text-green-400' : 'text-uf-textMuted'}`}>{label}</span>
    </label>
  );
}
